/**
 * 시스템 관리 API 클라이언트 (Phase 4-1) — backend /admin/* 와 1:1.
 * 모든 endpoint admin only.
 */

import { api, ApiCallError } from './api';
import type {
  AdminUserCreateInput,
  AdminUserRow,
  AdminUserUpdateInput,
  DataDeleteYearReport,
  DataReloadFiles,
  DataReloadReport,
  DataStatusCounts,
  DataYearsCategory,
  DataYearsResponse,
} from './types';

export async function listUsers(): Promise<{ users: AdminUserRow[] }> {
  return api.get('/admin/users');
}

/* ───── 정합성 점검 ───── */
export interface IntegrityCheck {
  key: string;
  label: string;
  count: number;
}
export interface IntegrityReport {
  critical: IntegrityCheck[];
  info: IntegrityCheck[];
  negativeStockItems: { itemCode: string; itemName: string | null; currentStock: number }[];
}
export async function getIntegrity(): Promise<IntegrityReport> {
  return api.get('/admin/integrity');
}

export interface IntegrityDetail {
  key: string;
  label: string;
  columns: { key: string; label: string }[];
  rows: Record<string, string | number | null>[];
}
/** 정합성 점검 발견 건 상세 (팝업용) */
export async function getIntegrityDetail(key: string): Promise<IntegrityDetail> {
  return api.get(`/admin/integrity/${encodeURIComponent(key)}`);
}

/* ───── 자동 백업(NAS) 기록 ───── */
export interface BackupRunRow {
  id: number;
  startedAt: string;
  finishedAt: string | null;
  status: string; // 'success' | 'failed' — 화면 표시는 성공/실패로 변환
  destPath: string | null;
  dbBytes: number;
  filesBytes: number;
  durationSec: number | null;
  deletedCount: number;
  message: string | null;
  appVersion: string | null;
}
export interface BackupStatusReport {
  schedule: string;
  destRoot: string;
  retentionDays: number;
  lastSuccessAt: string | null;
  hoursSinceLastSuccess: number | null;
  failStreak: number;
  healthy: boolean;
  rows: BackupRunRow[];
}
/** 매일 새벽 1시 자동 백업의 실행 기록 (최근 30건 + 요약) */
export async function getBackupStatus(): Promise<BackupStatusReport> {
  return api.get('/admin/backups');
}

export async function createUser(
  input: AdminUserCreateInput,
): Promise<{ userId: string }> {
  return api.post('/admin/users', input);
}

export async function updateUser(
  userId: string,
  input: AdminUserUpdateInput,
): Promise<{ userId: string }> {
  return api.patch(`/admin/users/${encodeURIComponent(userId)}`, input);
}

export async function resetUserPassword(
  userId: string,
  newPassword: string,
): Promise<{ reset: boolean }> {
  return api.post(
    `/admin/users/${encodeURIComponent(userId)}/reset-password`,
    { newPassword },
  );
}

/** 2단계 인증 초기화 — 휴대폰 분실 등 (v2.231.0 · F-11). 등급 basic · 암호키·복구코드 비움 · 신뢰 기기 해제 */
export async function resetUserTwoFactor(
  userId: string,
): Promise<{ reset: boolean; previousLevel: string; devicesRemoved: number }> {
  return api.post(`/admin/users/${encodeURIComponent(userId)}/reset-2fa`, {});
}

/* ───── 운영 데이터 재적재 (Phase 8-1) ───── */

export async function getDataStatus(): Promise<{ counts: DataStatusCounts }> {
  return api.get('/admin/data/status');
}

/* ───── 데이터 변경 이력 ───── */

export interface AuditLogRow {
  logId: number;
  tableName: string;
  rowPk: string;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  userId: string | null;
  userName: string | null;
  changedAt: string;
  /**
   * INSERT/DELETE 시 행 전체를 JSONB 로 보존 (rowDataSource='audit') 또는
   * 마이그레이션 이전 INSERT/UPDATE 행을 살아있는 현재 행에서 백필 (rowDataSource='current').
   * 복원 불가 행은 null.
   */
  rowData: Record<string, unknown> | null;
  /**
   * rowData 의 출처:
   *   - 'audit'   : 트리거가 보존한 그 시점 스냅샷 (시점 정확, 되돌리기 가능)
   *   - 'current' : 그 PK 의 현재 행을 백필 (현재 상태 — INSERT 이후 UPDATE 들 반영됨, 표시용)
   *   - null      : 행이 없거나 보강 불가 (마이그 이전 DELETE 등)
   */
  rowDataSource: 'audit' | 'current' | null;
  /** 되돌리기를 지원하는 표인가 (v2.231.0) — 옛 서버면 없다(그때는 종전처럼 단추를 단다) */
  revertable?: boolean;
}

export interface AuditLogParams {
  page?: number;
  pageSize?: number;
  tableName?: string;
  userId?: string;
  action?: 'INSERT' | 'UPDATE' | 'DELETE';
  from?: string;
  to?: string;
}

export interface AuditLogResponse {
  data: AuditLogRow[];
  page: { page: number; pageSize: number; total: number };
  distinctTables: string[];
}

export async function getAuditLog(
  params: AuditLogParams = {},
): Promise<AuditLogResponse> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.tableName) qs.set('tableName', params.tableName);
  if (params.userId) qs.set('userId', params.userId);
  if (params.action) qs.set('action', params.action);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  return api.get(`/admin/audit-log?${qs.toString()}`);
}

export interface AuditRevertResponse {
  action: 'reverted-insert' | 'reverted-delete' | 'reverted-update';
  table: string;
  rowPk: string;
  field?: string;
  oldValue?: string | null;
  rowsAffected: number;
}

export async function revertAuditLog(
  logId: number,
  force = false,
): Promise<AuditRevertResponse> {
  return api.post(`/admin/audit-log/${logId}/revert`, { force });
}

export async function reloadData(
  files: DataReloadFiles,
): Promise<{ report: DataReloadReport }> {
  const fd = new FormData();
  if (files.sales) fd.append('sales', files.sales, files.sales.name);
  if (files.purchase) fd.append('purchase', files.purchase, files.purchase.name);
  if (files.maintenance) fd.append('maintenance', files.maintenance, files.maintenance.name);
  if (files.bell) fd.append('bell', files.bell, files.bell.name);

  const res = await fetch('/api/admin/data/reload', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const json = await res.json();
  if (!json.ok) {
    throw new ApiCallError(
      json.error?.code ?? 'INTERNAL_ERROR',
      json.error?.message ?? '재적재 실패',
      json.error?.details,
      res.status,
    );
  }
  return json.data;
}

/* ───── 연도별 삭제 ───── */
export async function getDataYears(): Promise<DataYearsResponse> {
  return api.get('/admin/data/years');
}

export async function deleteDataByYear(
  category: DataYearsCategory,
  years: number[],
): Promise<DataDeleteYearReport> {
  return api.post('/admin/data/delete-year', { category, years });
}

/* ───── 포털 캘린더 일정 정리 (네이버웍스 중복 제거) ───── */
export interface CalendarCleanupTarget {
  sales_id: string;
  calendar_event_id: string;
  exp_date: string;
  status: string | null;
  end_user: string | null;
}
export interface CalendarCleanupResult {
  dryRun: boolean;
  before: string;
  count?: number;
  targets?: CalendarCleanupTarget[];
  deleted?: number;
  failed?: number;
  results?: { salesId: string; ok: boolean; error?: string }[];
}
/** before(YYYY-MM-DD) 이전 납품예정일의 포털 자동등록 일정 정리. dryRun=true 면 대상만 반환. */
export async function cleanupPortalCalendarEvents(
  before: string,
  dryRun: boolean,
): Promise<CalendarCleanupResult> {
  return api.post('/admin/cleanup-portal-calendar-events', { before, dryRun });
}

/* ───── 📅 남은 납품 일정 (v2.277.0) — 포털이 만들었는데 어느 영업건도 안 가리키는 웍스 일정 ───── */
export interface DeliveryOrphan {
  eventId: string;
  date: string;
  summary: string;
  memo: string;
  salesIds: string[];
  createdAt: string | null;
  /** 같은 날 같은 제목의 「포털이 가리키는 일정」이 있다 = 겹친 사본 */
  hasLiveTwin: boolean;
}
export function listDeliveryOrphans(from: string, to: string): Promise<{ from: string; to: string; scanned: number; orphans: DeliveryOrphan[] }> {
  return api.get(`/admin/calendar/delivery-orphans?from=${from}&to=${to}`);
}
export function deleteDeliveryOrphans(from: string, to: string, eventIds: string[]): Promise<{
  results: { eventId: string; ok: boolean; reason?: string; summary?: string; date?: string }[]; deleted: number;
}> {
  return api.post('/admin/calendar/delivery-orphans/delete', { from, to, eventIds });
}

/* ───── 📅 빠진 납품 일정 (v2.285.0) — 예정일이 있는데 웍스 일정이 없는 영업건 묶음 ───── */
export interface DeliveryMissingGroup {
  date: string;
  endUser: string;
  salesIds: string[];
  items: string[];
  /** 묶음 중 일정이 없는 영업건 수 / 전체 */
  missing: number;
  total: number;
}
export function listDeliveryMissing(from: string, to: string): Promise<{ from: string; to: string; enabled: boolean; groups: DeliveryMissingGroup[] }> {
  return api.get(`/admin/calendar/delivery-missing?from=${from}&to=${to}`);
}
export function createDeliveryMissing(from: string, to: string, groups: { date: string; endUser: string }[]): Promise<{
  results: { date: string; endUser: string; salesIds: string[]; ok: boolean; reason?: string }[]; created: number;
}> {
  return api.post('/admin/calendar/delivery-missing/create', { from, to, groups });
}

/* ───── 거래처 마스터 ───── */
export type PartnerKind = 'supplier' | 'customer' | 'both';

export interface PartnerCompanyRow {
  id: number;
  name: string;
  autoFix: boolean;
  isActive: boolean;
  note: string | null;
  businessNumber: string | null;
  address: string | null;
  kind: PartnerKind;
  /** 세금계산서 발행 이메일 기본값 (최대 2개) — 발행요청 양식 자동완성용 */
  taxInvoiceEmail: string | null;
  taxInvoiceEmail2: string | null;
  certAttachmentId: number | null;
  certFilename: string | null;
  bankbookAttachmentId: number | null;
  bankbookFilename: string | null;
  updatedAt: string;
  /**
   * 이 거래처를 **번호로** 물고 있는 줄 수 — 지우면 그 연결이 끊긴다 (v2.163.0).
   * 삭제 확인 문구에서 실제로 몇 건이 끊기는지 알려 주는 데 쓴다.
   */
  contactCount: number;
  dealCount: number;
  /** 명절선물 받는 곳 줄 수(v2.247.0) — 옛 서버(배포 도중)는 안 준다 */
  giftCount?: number;
}

export async function listPartnerCompanies(): Promise<{ items: PartnerCompanyRow[] }> {
  return api.get('/admin/partner-companies');
}

/* ───── 사업자등록증 사본 자동 판독 (거래처 추가 자동완성) ───── */
export interface PartnerCertParseResult {
  bizNo: string | null;    // 사업자등록번호 (###-##-#####)
  name: string | null;     // 상호(법인명)
  address: string | null;  // 사업장 소재지
  email: string | null;    // 사본에 기재 시
  source: 'pdf-text' | 'ocr';
}

export async function parsePartnerCert(file: File): Promise<PartnerCertParseResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch('/api/admin/partner-companies/parse-cert', {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  // 프록시 오류(413/502 등)는 JSON 이 아닐 수 있음 — 파싱 실패 시 HTTP 상태로 안내
  type ParseEnvelope = { ok: true; data: PartnerCertParseResult } | { ok: false; error?: { message?: string } };
  let json: ParseEnvelope | null = null;
  try {
    json = (await res.json()) as ParseEnvelope;
  } catch { /* non-JSON */ }
  if (!json || typeof json.ok !== 'boolean') {
    throw new Error(res.status === 413 ? '파일이 너무 큽니다 (서버 상한 초과)' : `판독 요청 실패 (HTTP ${res.status})`);
  }
  if (!json.ok) throw new Error(json.error?.message ?? `판독 실패 (HTTP ${res.status})`);
  return json.data;
}

export async function createPartnerCompany(input: {
  name: string;
  autoFix?: boolean;
  note?: string | null;
  businessNumber?: string | null;
  address?: string | null;
  kind?: PartnerKind;
  taxInvoiceEmail?: string | null;
  taxInvoiceEmail2?: string | null;
  /** 같은 사업자번호·비슷한 이름 경고를 보고 「그래도 저장」 (v2.231.0 · D-3) */
  confirmDuplicate?: boolean;
}): Promise<{ id: number }> {
  return api.post('/admin/partner-companies', input);
}

export async function updatePartnerCompany(
  id: number,
  input: {
    name?: string;
    autoFix?: boolean;
    isActive?: boolean;
    note?: string | null;
    businessNumber?: string | null;
    address?: string | null;
    kind?: PartnerKind;
    taxInvoiceEmail?: string | null;
    taxInvoiceEmail2?: string | null;
    confirmDuplicate?: boolean;
  },
): Promise<{ id: number }> {
  return api.patch(`/admin/partner-companies/${id}`, input);
}

export async function deletePartnerCompany(id: number): Promise<{ deleted: boolean }> {
  return api.delete(`/admin/partner-companies/${id}`);
}

/* ── 거래처 상세 통합뷰 (연결 거래) ── */
export interface PartnerSalesRow {
  salesId: string;
  proposalCompany: string | null;
  invoiceCompany: string | null;
  endUser: string | null;
  projectName: string | null;
  proposalPrice: number | null;
  status: string | null;
  contractDate: string;
  invoiceDate: string;
}
export interface PartnerPurchaseRow {
  purchaseId: number;
  supplier: string | null;
  invoiceCompany: string | null;
  endUser: string | null;
  projectName: string | null;
  itemGroup: string | null;
  description: string | null;
  amount: number | null;
  orderDate: string;
}
export interface PartnerMaintRow {
  contractId: number;
  contractNo: string | null;
  contractName: string | null;
  endUser: string | null;
  contractCompany: string | null;
  awardCompany: string | null;
  status: string | null;
  contractStart: string;
  contractEnd: string;
}
export interface PartnerQuoteRow {
  id: number;
  quoteNo: string | null;
  recipientCompany: string | null;
  projectName: string | null;
  totalWithVat: number | null;
  quoteDate: string;
}
/* v2.318.0 거래처 360 — 관련 목록 */
export interface PartnerDealRow {
  id: number; vendor: string | null; registrationNo: string | null; status: string | null; project: string | null;
  endUser: string | null; tier2: string | null; expireDate: string | null; dday: number | null; ownerName: string | null; salesId: string | null;
}
export interface PartnerDeliveryRow {
  salesId: string; projectName: string | null; endUser: string | null; status: string | null;
  expectedDeliveryDate: string | null; startTime: string; deliveryDate: string; deliveryPlace: string | null;
}
export interface PartnerMemoRow { salesId: string; projectName: string | null; content: string; createdAt: string; userName: string | null }
export interface PartnerGiftRow {
  id: number; year: number; kind: string; owner: string | null; recipient: string | null; itemName: string; qty: number | null; method: string; isDone: boolean;
}
export interface PartnerMailRow { id: number; subject: string | null; toAddrs: string | null; createdAt: string; sender: string | null }
export interface PartnerAttachmentRow { id: number; salesId: string; kind: string; filename: string; uploadedAt: string; projectName: string | null }
export interface PartnerHighlights {
  yearSales: number; yearCount: number; openCount: number; openAmount: number; maintOpen: number; lastTouch: string | null;
}
export interface PartnerCompanyDetail {
  partner: {
    id: number;
    name: string;
    note: string | null;
    businessNumber: string | null;
    address: string | null;
    kind: PartnerKind;
  };
  sales: { total: number; rows: PartnerSalesRow[] };
  purchase: { total: number; rows: PartnerPurchaseRow[] };
  maintenance: { total: number; rows: PartnerMaintRow[] };
  quote: { total: number; rows: PartnerQuoteRow[] };
  highlights: PartnerHighlights;
  deal: { total: number; rows: PartnerDealRow[] };
  delivery: { total: number; rows: PartnerDeliveryRow[] };
  memo: { total: number; rows: PartnerMemoRow[] };
  gift: { total: number; rows: PartnerGiftRow[] };
  mail: { total: number; rows: PartnerMailRow[] };
  attachment: { total: number; rows: PartnerAttachmentRow[] };
}
export async function getPartnerCompanyDetail(id: number): Promise<PartnerCompanyDetail> {
  return api.get(`/admin/partner-companies/${id}/detail`);
}
